import React, { FC, useContext } from 'react'
import { Input, Select } from 'antd'
import { ConditionOperatorType, FilterType } from '../enums'
import {
    CustomFilter,
    Filter,
    filterIsAnyOfOrAllOf,
    filterIsNumerical,
    filterIsFreeText,
    FreeTextFilter,
    NumericalFilter,
    filterIsDateType,
    SpecificDateFilter,
    filterIsSpecificDate,
    filterIsRelativeDate,
    RelativeDateFilter,
} from '../types'
import { FreeTextInputFilter } from './blueconic-free-text-filter'
import { TaggedInputFilter } from './tagged-input-filter'
import { simpleDateFormat } from '../helpers'
import { BlueConicConfigurationContext } from '../../configuration/context'
import { SpecificDateFilterValueBuilder } from './specific-date-value-builder'

type CustomFilterBuilderProps = {
    filter: CustomFilter
    onChange: (value: Filter) => void
}

type CustomFilterOperator =
    | ConditionOperatorType.EQUALS
    | ConditionOperatorType.LT
    | ConditionOperatorType.LTE
    | ConditionOperatorType.GT
    | ConditionOperatorType.GTE

type CustomFilterTypeDynamic =
    | `${FilterType.NUMERICAL}_${CustomFilterOperator}`
    | `${FilterType.SPECIFIC_DATE}_${CustomFilterOperator}`
    | `${FilterType.RELATIVE_DATE}_${ConditionOperatorType.LTE | ConditionOperatorType.GT}`

type CustomFilterTypes =
    | FilterType.FREE_TEXT
    | FilterType.MATCHES_ANY_OF
    | FilterType.MATCHES_ALL_OF
    | CustomFilterTypeDynamic

// only key:value property with free text field enabled v1
const customFilterBuilderOptions = [
    {
        label: 'Text',
        options: [
            { value: FilterType.FREE_TEXT, label: 'equals' },
            { value: FilterType.MATCHES_ALL_OF, label: 'matches all of' },
            { value: FilterType.MATCHES_ANY_OF, label: 'matches any of' },
        ],
    },
    {
        label: 'Number',
        options: [
            { value: `${FilterType.NUMERICAL}_${ConditionOperatorType.EQUALS}`, label: 'is equal to (number)' },
            { value: `${FilterType.NUMERICAL}_${ConditionOperatorType.LT}`, label: 'is less than' },
            { value: `${FilterType.NUMERICAL}_${ConditionOperatorType.LTE}`, label: 'is less than or equal to' },
            { value: `${FilterType.NUMERICAL}_${ConditionOperatorType.GT}`, label: 'is greater than or equal to' },
            { value: `${FilterType.NUMERICAL}_${ConditionOperatorType.GTE}`, label: 'is greater than' },
        ],
    },
    {
        label: 'Date (specific)',
        options: [
            { value: `${FilterType.SPECIFIC_DATE}_${ConditionOperatorType.EQUALS}`, label: 'is equal to (date)' },
            { value: `${FilterType.SPECIFIC_DATE}_${ConditionOperatorType.LT}`, label: 'is newer than' },
            { value: `${FilterType.SPECIFIC_DATE}_${ConditionOperatorType.LTE}`, label: 'is newer than or equal to' },
            { value: `${FilterType.SPECIFIC_DATE}_${ConditionOperatorType.GT}`, label: 'is older than or equal to' },
            { value: `${FilterType.SPECIFIC_DATE}_${ConditionOperatorType.GTE}`, label: 'is older than' },
        ],
    },
    {
        label: 'Date (relative)',
        options: [
            { value: `${FilterType.RELATIVE_DATE}_${ConditionOperatorType.LTE}`, label: 'is within the last' },
            { value: `${FilterType.RELATIVE_DATE}_${ConditionOperatorType.GT}`, label: 'is not within the last' },
        ],
    },
]

/** @todo - when implemented ensure ViewOnly context is implemented */
export const CustomFilterBuilder: FC<CustomFilterBuilderProps> = ({ filter, onChange }) => {
    const context = useContext(BlueConicConfigurationContext)

    const value = filter.nestedFilter.value
    const typeValue = filterIsNumerical(filter.nestedFilter)
        ? (`${filter.nestedFilter.filterType}_${filter.nestedFilter.value.operator}` as CustomFilterTypeDynamic)
        : filterIsDateType(filter.nestedFilter)
        ? (`${filter.nestedFilter.filterType}_${filter.nestedFilter.value.operator}` as CustomFilterTypeDynamic)
        : (filter.nestedFilter.filterType as CustomFilterTypes)

    return (
        <>
            <Input
                className="blueconic-custom-filter-property-input"
                placeholder="property"
                value={filter.property}
                onChange={(ev) => {
                    onChange({
                        ...filter,
                        property: ev.target.value,
                    })
                }}
            />

            <Select<CustomFilterTypes>
                className="blueconic-custom-filter-operator-select-select"
                dropdownMatchSelectWidth={false}
                size="middle"
                options={customFilterBuilderOptions}
                value={typeValue}
                onChange={(v) => {
                    const nextFilterValue: CustomFilter['nestedFilter'] = { ...filter.nestedFilter }

                    if (
                        [FilterType.FREE_TEXT, FilterType.MATCHES_ANY_OF, FilterType.MATCHES_ALL_OF].includes(v as any)
                    ) {
                        nextFilterValue.filterType = v as
                            | FilterType.FREE_TEXT
                            | FilterType.MATCHES_ANY_OF
                            | FilterType.MATCHES_ALL_OF

                        if (filterIsAnyOfOrAllOf(nextFilterValue)) {
                            nextFilterValue.value = Array.isArray(value) ? value : []
                        } else if (filterIsFreeText(nextFilterValue)) {
                            nextFilterValue.value = typeof value === 'string' ? value : undefined
                        }
                    } else {
                        const [type, operator] = (v as CustomFilterTypeDynamic).split('_')

                        nextFilterValue.filterType = parseInt(type, 10) as
                            | FilterType.NUMERICAL
                            | FilterType.SPECIFIC_DATE
                            | FilterType.RELATIVE_DATE

                        if (filterIsNumerical(nextFilterValue)) {
                            nextFilterValue.value = {
                                operator: operator as
                                    | ConditionOperatorType.LT
                                    | ConditionOperatorType.LTE
                                    | ConditionOperatorType.GT
                                    | ConditionOperatorType.GTE,
                                value: 1,
                            }
                        } else if (filterIsRelativeDate(nextFilterValue)) {
                            nextFilterValue.value = {
                                operator: operator as ConditionOperatorType.LTE | ConditionOperatorType.GT,
                                value: 7,
                                meta: {
                                    timezone: context.domain.timezone,
                                    qualifier: 'days',
                                },
                            } as any
                        } else if (filterIsDateType(nextFilterValue)) {
                            nextFilterValue.value = {
                                operator,
                                value: simpleDateFormat(new Date()),
                                meta: {
                                    timezone: context.domain.timezone,
                                    qualifier: 'days',
                                },
                            } as any
                        }
                    }

                    onChange({
                        ...filter,
                        blueConicType: filter.blueConicType,
                        nestedFilter: nextFilterValue,
                    })
                }}
            />

            <div className="blueconic-custom-filter-value-row">
                {filterIsNumerical(filter.nestedFilter) && (
                    <Input
                        className="blueconic-numerical-filter-value-input"
                        type="number"
                        value={filter.nestedFilter.value.value}
                        onChange={(ev) =>
                            onChange({
                                ...filter,
                                nestedFilter: {
                                    ...filter.nestedFilter,
                                    value: {
                                        ...(filter.nestedFilter as NumericalFilter).value,
                                        value: parseInt(ev.target.value, 10),
                                    },
                                } as NumericalFilter,
                            })
                        }
                    />
                )}
                {filterIsAnyOfOrAllOf(filter.nestedFilter) && (
                    <TaggedInputFilter
                        filter={filter.nestedFilter}
                        onChange={(change) => onChange({ ...filter, nestedFilter: change })}
                        nested={true}
                    />
                )}
                {filterIsFreeText(filter.nestedFilter) && (
                    <FreeTextInputFilter
                        filter={filter.nestedFilter}
                        onChange={(change) =>
                            onChange({
                                ...filter,
                                nestedFilter: { ...filter.nestedFilter, value: change } as FreeTextFilter,
                            })
                        }
                    />
                )}
                {filterIsSpecificDate(filter.nestedFilter) && (
                    <SpecificDateFilterValueBuilder
                        showOperator={false}
                        filter={filter.nestedFilter satisfies SpecificDateFilter}
                        onChange={(change) =>
                            onChange({
                                ...filter,
                                nestedFilter: change,
                            })
                        }
                    />
                )}
                {filterIsRelativeDate(filter.nestedFilter) &&
                    (() => {
                        const relValue = filter.nestedFilter.value as RelativeDateFilter['value']
                        return (
                            <>
                                <Input
                                    className="blueconic-numerical-filter-value-input"
                                    type="number"
                                    value={relValue.value}
                                    onChange={(ev) => {
                                        if (!ev.target.value) return
                                        onChange({
                                            ...filter,
                                            nestedFilter: {
                                                filterType: filter.nestedFilter.filterType,
                                                value: { ...relValue, value: parseInt(ev.target.value, 10) },
                                            },
                                        } as CustomFilter)
                                    }}
                                />
                                <Select<'hours' | 'days' | 'weeks' | 'months'>
                                    size="middle"
                                    value={relValue.meta.qualifier}
                                    onChange={(qualifier) =>
                                        onChange({
                                            ...filter,
                                            nestedFilter: {
                                                filterType: filter.nestedFilter.filterType,
                                                value: { ...relValue, meta: { ...relValue.meta, qualifier } },
                                            },
                                        } as CustomFilter)
                                    }
                                    options={[
                                        { value: 'hours', label: 'hours' },
                                        { value: 'days', label: 'days' },
                                        { value: 'weeks', label: 'weeks' },
                                        { value: 'months', label: 'months' },
                                    ]}
                                />
                            </>
                        )
                    })()}
            </div>
        </>
    )
}
